// src/inquiries/InquiryEdit.tsx

import React, { FC, useState, useEffect } from 'react';
import {
    Edit,
    SimpleForm,
    Toolbar,
    SaveButton,
    TextInput,
    DateInput,
    NumberInput,
    ReferenceInput,
    SelectInput,
    required,
    useNotify,
    useRedirect,
    TopToolbar,
    usePermissions,
    useDelete,
    useRecordContext,
    FormDataConsumer,
    useDataProvider,
} from 'react-admin';
import {
    Button as MUIButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';

const PAYMENT_TERM_CHOICES = [
    { id: 'Payment Before /Against Loading', name: 'Payment Before /Against Loading' },
    { id: 'Credit Days Against PDC', name: 'Credit Days Against PDC' },
    { id: 'Order Cancel', name: 'Order Cancel' },
];

const InquiryCommercialFields: FC<{ approved: boolean; formData: any }> = ({ approved, formData }) => {
    const dataProvider = useDataProvider();
    const [states, setStates] = useState<{ id: string; state_code: string; state_name: string }[]>([]);

    useEffect(() => {
        if (!approved) return;
        dataProvider
            .getList('states', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'state_name', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => setStates(data))
            .catch(() => setStates([]));
    }, [approved, dataProvider]);

    if (!approved) return null;

    return (
        <>
            <NumberInput
                source="order_company_price_per_kg"
                label="Company price (₹/kg)"
                helperText="Internal only — not shown to customer"
            />
            <NumberInput
                source="order_commercial_credit_note_discount"
                label="Credit note (₹/kg)"
            />
            <NumberInput source="order_delivery_charges" label="Freight / delivery (₹)" />
            <SelectInput
                source="payment_terms_label"
                label="Payment terms"
                choices={PAYMENT_TERM_CHOICES}
                validate={[required()]}
            />
            {formData?.payment_terms_label === 'Credit Days Against PDC' && (
                <NumberInput source="payment_term_days" label="Credit days" />
            )}
            <SelectInput
                source="order_state_code"
                label="State"
                choices={states}
                optionText="state_name"
                optionValue="state_code"
                validate={[required()]}
            />
        </>
    );
};

// Normalize permission entries into ['Admin','ProductAdmin',…']
const normalizeRoles = (perms: any): string[] => {
    const arr = Array.isArray(perms) ? perms : perms != null ? [perms] : [];
    return arr.map(p =>
        typeof p === 'number'
            ? (UserRole[p] as unknown as string)
            : String(p)
    );
};

// Custom Delete button: Admin sees confirmation+delete; ProductAdmin sees denied dialog
const CustomDeleteButton: FC = () => {
    const record = useRecordContext<{ id: number }>();
    const { permissions } = usePermissions<any>();
    const roles = normalizeRoles(permissions);
    const isAdmin = roles.includes('Admin');
    const isProductAdmin = roles.includes('ProductAdmin');

    const [deleteOne, { isLoading }] = useDelete();
    const notify = useNotify();
    const redirect = useRedirect();
    const [openConfirm, setOpenConfirm] = useState(false);
    const [openDenied, setOpenDenied] = useState(false);

    const handleClick = () => {
        if (isAdmin) setOpenConfirm(true);
        else if (isProductAdmin) setOpenDenied(true);
    };

    const confirmDelete = async () => {
        await deleteOne('inquiries', { id: record.id });
        notify('Inquiry deleted', { type: 'info' });
        setOpenConfirm(false);
        redirect('/inquiries');
    };

    return (
        <>
            <MUIButton
                variant="text"
                startIcon={<DeleteIcon />}
                onClick={handleClick}
                disabled={isLoading}
                sx={{ textTransform: 'none' }}
            >
                Delete
            </MUIButton>

            {/* Admin confirmation */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this inquiry?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MUIButton onClick={() => setOpenConfirm(false)}>
                        Cancel
                    </MUIButton>
                    <MUIButton color="error" onClick={confirmDelete}>
                        Delete
                    </MUIButton>
                </DialogActions>
            </Dialog>

            {/* ProductAdmin denied */}
            <Dialog open={openDenied} onClose={() => setOpenDenied(false)}>
                <DialogTitle>Permission Denied</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You don't have permission to delete inquiries. Please contact your administrators.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MUIButton onClick={() => setOpenDenied(false)}>
                        OK
                    </MUIButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Top toolbar: Back + conditional Delete
const InquiryEditActions: FC<any> = props => {
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);
    const { permissions } = usePermissions<any>();
    const roles = normalizeRoles(permissions);
    const isAdmin = roles.includes('Admin');
    const isProductAdmin = roles.includes('ProductAdmin');
    const redirect = useRedirect();

    return (
        <TopToolbar>
            {(isAdmin || isProductAdmin) && <CustomDeleteButton />}
        </TopToolbar>
    );
};

// Bottom toolbar: Save then Back
const InquiryEditToolbar: FC<any> = props => {
    const redirect = useRedirect();
    return (
        <Toolbar {...props}>
            <SaveButton />
        </Toolbar>
    );
};

export const InquiryEdit: FC<any> = props => {
    const notify = useNotify();
    const redirect = useRedirect();

    const onSuccess = () => {
        notify('Inquiry updated successfully', { type: 'info' });
        redirect('/inquiries');
    };

    // Existing validators
    const validateDecimal = (message: string) => (value: any) => {
        if (value == null || value === '') return;
        const regex = /^\d+(\.\d{1,2})?$/;
        return regex.test(String(value)) ? undefined : message;
    };

    const parseQuantity = (value: any) => {
        if (value === '' || value === null || value === undefined) return value;
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : value;
    };

    const formatQuantity = (value: any) => {
        if (value === '' || value === null || value === undefined) return '';
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : '';
    };

    const isApprovedStatus = (formData: any) => Number(formData?.inquiry_status_id) === 2;

    const validateAcceptedQuantity = (value: any, formData: any) => {
        if (isApprovedStatus(formData)) {
            if (value == null || value === '' || Number(value) <= 0) {
                return 'Accepted Qty is required when accepting an inquiry';
            }
            return validateDecimal('Not a valid Qty')(value);
        }
        if (value != null && value !== '') {
            return validateDecimal('Not a valid Qty')(value);
        }
        return undefined;
    };

    const validateAcceptedPrice = (value: any, formData: any) => {
        if (isApprovedStatus(formData)) {
            if (value == null || value === '' || Number(value) <= 0) {
                return 'Accepted Price is required when accepting an inquiry';
            }
            return validateDecimal('Not a valid Price')(value);
        }
        if (value != null && value !== '') {
            return validateDecimal('Not a valid Price')(value);
        }
        return undefined;
    };

    return (
        <Edit
            {...props}
            actions={<InquiryEditActions />}
            mutationMode="pessimistic"
            onSuccess={onSuccess}
        >
            <SimpleForm toolbar={<InquiryEditToolbar />}>
                <NumberInput source="id" disabled label="Inquiry Id" />
                <TextInput source="inquiry_date" disabled label="Date" />
                <NumberInput source="inquiry_price" disabled label="Price" />
                <NumberInput
                    source="inquiry_quantity"
                    disabled
                    label="Inquiry Qty (MT)"
                    step={0.01}
                    format={formatQuantity}
                    parse={parseQuantity}
                />
                <ReferenceInput source="inquiry_status_id" reference="inquiries/getStatus" alwaysOn>
                    <SelectInput
                        optionText="inquiry_status"
                        label="Inquiry Status"
                        validate={[required()]}
                    />
                </ReferenceInput>
                <FormDataConsumer>
                    {({ formData, ...rest }) => {
                        const approved = isApprovedStatus(formData);
                        return (
                            <>
                                <NumberInput
                                    source="accepted_inquiry_quantity"
                                    label="Accepted Qty (MT)"
                                    step={0.01}
                                    format={formatQuantity}
                                    parse={parseQuantity}
                                    isRequired={approved}
                                    helperText={
                                        approved
                                            ? 'Required when accepting (Approved). Decimals allowed, e.g. 2.4 MT'
                                            : 'Decimals allowed, e.g. 2.4 MT'
                                    }
                                    validate={(value: any) =>
                                        validateAcceptedQuantity(value, formData)
                                    }
                                    {...rest}
                                />
                                <NumberInput
                                    source="accepted_inquiry_price"
                                    label="Selling rate (₹/kg)"
                                    isRequired={approved}
                                    helperText={
                                        approved
                                            ? 'Required when accepting (Approved)'
                                            : undefined
                                    }
                                    validate={(value: any) =>
                                        validateAcceptedPrice(value, formData)
                                    }
                                    {...rest}
                                />
                                <InquiryCommercialFields approved={approved} formData={formData} />
                            </>
                        );
                    }}
                </FormDataConsumer>
            </SimpleForm>
        </Edit>
    );
};

export default InquiryEdit;

